import { useMemo, useReducer } from 'react';
import type { SubgroupFormData } from './SubgroupFormConfig';
import { defaultSubgroupFormValues } from './SubgroupFormConfig';

export type FormMode = 'create' | 'edit';

// State interface
export interface SubgroupFormState {
    formData: SubgroupFormData;
    mode: FormMode;
    isSubmitting: boolean;
}

// Actions interface
export interface SubgroupFormActions {
    updateFormData: (data: Partial<SubgroupFormData>) => void;
    submit: (data: Partial<SubgroupFormData>) => void;
    submitStart: () => void;
    submitSuccess: () => void;
    submitError: () => void;
    reset: () => void;
    initialize: (data: Partial<SubgroupFormData>) => void;
}

type SubgroupFormAction =
    | { type: 'UPDATE_FORM_DATA'; payload: Partial<SubgroupFormData> }
    | { type: 'SUBMIT'; payload: Partial<SubgroupFormData> }
    | { type: 'SUBMIT_START' }
    | { type: 'SUBMIT_SUCCESS' }
    | { type: 'SUBMIT_ERROR' }
    | { type: 'RESET' }
    | { type: 'INITIALIZE'; payload: Partial<SubgroupFormData> };

function subgroupFormReducer(state: SubgroupFormState, action: SubgroupFormAction): SubgroupFormState {
    switch (action.type) {
        case 'UPDATE_FORM_DATA':
            return {
                ...state,
                formData: { ...state.formData, ...action.payload },
            };
        case 'SUBMIT':
            return {
                ...state,
                isSubmitting: true,
            };
        case 'SUBMIT_START':
            return {
                ...state,
                isSubmitting: true,
            };
        case 'SUBMIT_SUCCESS':
        case 'SUBMIT_ERROR':
            return {
                ...state,
                isSubmitting: false,
            };
        case 'RESET':
            return {
                ...state,
                formData: defaultSubgroupFormValues,
                isSubmitting: false,
            };
        case 'INITIALIZE':
            return {
                ...state,
                formData: { ...defaultSubgroupFormValues, ...action.payload },
            };
        default:
            return state;
    }
}

export function useSubgroupFormState(mode: FormMode, initialData?: Partial<SubgroupFormData>) {
    // Initialize state
    const initialState: SubgroupFormState = {
        formData: initialData ? { ...defaultSubgroupFormValues, ...initialData } : defaultSubgroupFormValues,
        mode,
        isSubmitting: false,
    };

    // Create reducer
    const [state, dispatch] = useReducer(subgroupFormReducer, initialState);
    const actions = useMemo<SubgroupFormActions>(
        () => ({
            updateFormData: data => dispatch({ type: 'UPDATE_FORM_DATA', payload: data }),
            submit: data => dispatch({ type: 'SUBMIT', payload: data }),
            submitStart: () => dispatch({ type: 'SUBMIT_START' }),
            submitSuccess: () => dispatch({ type: 'SUBMIT_SUCCESS' }),
            submitError: () => dispatch({ type: 'SUBMIT_ERROR' }),
            reset: () => dispatch({ type: 'RESET' }),
            initialize: data => dispatch({ type: 'INITIALIZE', payload: data }),
        }),
        [dispatch]
    );

    return { state, actions };
}
